import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import type { TourDocument, VariantAxis } from "@heroncendre/schema";
import {
  pickText,
  useCoarsePointer,
  usePanoramaProgress,
  useTourState,
  useTourStoreApi,
} from "@heroncendre/player";
import { IconeAmbianceSvg } from "./ambianceIcones.js";
import ambiances from "./ambiances.json";
import chainesJson from "./chaines.json";
import { useEtatNavigation } from "./etatNavigation.js";
import { COULEURS, POLICE_UI_SPLASH } from "./splashStyles.js";
import { useObstacleBas } from "./useObstacleBas.js";
import { usePrechargementVersions } from "./usePrechargementVersions.js";
import { useReducedMotion } from "./useReducedMotion.js";
import "./variantSwitcher.css";

/**
 * ============================================================================
 *  VariantSwitcher.tsx — Le contrôleur d'ambiances
 * ----------------------------------------------------------------------------
 *  Change la version du panorama d'une scène qui en propose plusieurs (jour,
 *  coucher de soleil, soir, nuit…), en barre au bas de l'écran. Référence de
 *  design : `sources/design/design_handoff_controleur_ambiances/` (thème Écrin).
 *
 *  ── Les données sont celles du moteur ──────────────────────────────────────
 *
 *  Le handoff décrivait des champs `variants` / `variantGroupKey` /
 *  `defaultVariant` ; ils existent déjà sous d'autres noms, et toute la visite
 *  en dépend (moments des parcours, éditeur, fondu sur place) : un AXE du tour
 *  (l'intitulé, ses valeurs et leurs libellés FR/EN, dans `tour.json`), et les
 *  VERSIONS de la scène, tuilées, une par valeur. La version par défaut est la
 *  première de la scène. Seul le pictogramme de chaque valeur, affaire de
 *  présentation, vit côté coquille (`ambiances.json`).
 *
 *  Une barre par axe qui offre un vrai choix (au moins deux versions dans la
 *  scène). Aucune : rien n'est rendu.
 *
 *  ── Le clic ────────────────────────────────────────────────────────────────
 *
 *  Le bouton passe sélectionné tout de suite, et sa barre sauge suit la VRAIE
 *  progression du niveau de base de la version demandée (`usePanoramaProgress`).
 *  Le moteur garde la scène affichée jusqu'à ce que la nouvelle soit prête, puis
 *  les fond l'une dans l'autre en 0,9 s, caméra inchangée (`setVariantSelector`).
 *  Un second clic remplace la demande : c'est le dernier choix qui gagne.
 *
 *  ── La rotation ────────────────────────────────────────────────────────────
 *
 *  À l'arrivée sur la scène, les ambiances DÉFILENT seules, dans l'ordre de
 *  l'axe et en boucle : chacune reste affichée 5 s (`rotationSecondes`, compté
 *  une fois la version à l'écran), puis se fond dans la suivante par un fondu
 *  enchaîné plus lent qu'au clic (`fonduRotationSecondes`). Le visiteur voit
 *  ainsi le lieu changer de lumière sans avoir à chercher la barre.
 *
 *  Le premier CHOIX du visiteur (clic, Entrée) met la rotation en pause : il a
 *  pris la main, on ne lui reprend pas. Elle reprend à la prochaine arrivée sur
 *  la scène. Les changements automatiques ne sont
 *  pas annoncés aux lecteurs d'écran : une annonce toutes les cinq secondes
 *  serait du bruit ; seul un choix l'est.
 *
 *  ── Clavier ────────────────────────────────────────────────────────────────
 *
 *  Tabindex itinérant : seul le bouton sélectionné est dans l'ordre de
 *  tabulation ; flèches, Début et Fin déplacent le focus SANS rien charger,
 *  Entrée ou Espace valide. Les flèches sont consommées ici (`preventDefault`) :
 *  elles ne tournent pas la vue (`FlechesRotation`) tant que le focus est dans
 *  la barre.
 * ==========================================================================*/

const ANNONCE: Record<string, string> = {
  fr: chainesJson.fr.variantAnnouncement,
  en: chainesJson.en.variantAnnouncement,
};

/** Au-delà, on cesse d'attendre l'avis du moteur (le sien abandonne à 4 s). */
const ATTENTE_MAX_MS = 8000;

interface Axe {
  axis: VariantAxis;
  valeurs: VariantAxis["values"];
  declarees: Set<string>;
  /** La version que le moteur affiche quand le choix demandé n'existe pas ici. */
  parDefaut: string | undefined;
}

/** Ce que la scène propose, axe par axe : seules les valeurs qu'elle déclare. */
function axesProposes(tour: TourDocument, sceneId: string): Axe[] {
  const scene = tour.scenes.find((candidate) => candidate.id === sceneId);
  if (!scene) return [];
  const axes: Axe[] = [];
  for (const axisId of scene.activeAxes) {
    const axis = tour.axes.find((candidate) => candidate.id === axisId);
    if (!axis) continue;
    const declarees = new Set<string>();
    for (const visual of scene.visuals) {
      const value = visual.selector[axisId];
      if (value !== undefined) declarees.add(value);
    }
    if (declarees.size < 2) continue;
    axes.push({
      axis,
      valeurs: [...axis.values].filter((value) => declarees.has(value.id)).sort((a, b) => a.order - b.order),
      declarees,
      parDefaut: scene.visuals[0]?.selector[axisId],
    });
  }
  return axes;
}

export function VariantSwitcher({ assetBaseUrl }: { assetBaseUrl: string }) {
  const tour = useTourState((s) => s.tour);
  const sceneId = useTourState((s) => s.currentSceneId);
  const mobile = useCoarsePointer();
  // La feuille de navigation ouverte prend le bas de l'écran, sur téléphone.
  const sousFeuille = useEtatNavigation((e) => e.ouvert);
  const pileRef = useRef<HTMLDivElement>(null);
  // La pause de la rotation est tenue ici, pas dans la barre : sur téléphone, la
  // barre disparaît sous la feuille de navigation et se remonte ensuite.
  const [pauseSur, setPauseSur] = useState<string | null>(null);
  useEffect(() => setPauseSur(null), [sceneId]);
  usePrechargementVersions(assetBaseUrl);

  const axes = axesProposes(tour, sceneId);
  const present = axes.length > 0 && !(mobile && sousFeuille);
  // Le titre d'arrivée se pose au-dessus de la barre (`SceneTitle`).
  useObstacleBas("ambiances", pileRef, present);
  if (!present) return null;

  return (
    <>
      {/* Contraste AA des libellés sur un panorama clair (jour, ciel). */}
      <div aria-hidden style={voileBas} />
      <div ref={pileRef} style={pile}>
        {axes.map((axe) => (
          // Une barre neuve par scène : demande en cours et focus n'ont pas de sens d'une scène à l'autre.
          <Barre
            key={`${sceneId}|${axe.axis.id}`}
            axe={axe}
            sceneId={sceneId}
            enPause={pauseSur === sceneId}
            onPause={() => setPauseSur(sceneId)}
          />
        ))}
      </div>
    </>
  );
}

function Barre({
  axe,
  sceneId,
  enPause,
  onPause,
}: {
  axe: Axe;
  sceneId: string;
  /** Le visiteur a choisi sur cette scène : plus de rotation jusqu'à la prochaine arrivée. */
  enPause: boolean;
  onPause: () => void;
}) {
  const store = useTourStoreApi();
  const language = useTourState((s) => s.context.language);
  const demandee = useTourState((s) => s.context.selector[axe.axis.id]);
  const progression = usePanoramaProgress();
  const mobile = useCoarsePointer();
  const reduit = useReducedMotion();
  const piste = useRef<HTMLDivElement>(null);

  /** Demande en cours : la valeur, la clé de source (`scène|axes`) que le moteur chargera, et si c'est la rotation. */
  const [attente, setAttente] = useState<{ valeur: string; cle: string; auto: boolean } | null>(null);
  const [annonce, setAnnonce] = useState("");

  const effective = demandee !== undefined && axe.declarees.has(demandee) ? demandee : axe.parDefaut;
  const intitule = pickText(axe.axis.label, language) ?? axe.axis.id;
  const libelle = (id: string) => pickText(axe.valeurs.find((v) => v.id === id)?.label, language) ?? id;

  // La version demandée est là (ou introuvable) : fin de l'attente, et annonce.
  const concerne = attente !== null && progression.sourceKey === attente.cle;
  const fini = concerne && progression.status !== "pending";
  useEffect(() => {
    if (!fini || !attente) return;
    if (attente.auto) return setAttente(null);
    setAnnonce(
      (ANNONCE[language] ?? ANNONCE.fr ?? "{axis} : {value}")
        .replace("{axis}", intitule)
        .replace("{value}", libelle(attente.valeur)),
    );
    setAttente(null);
    // `libelle` et `intitule` découlent de la langue, déjà suivie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fini, attente, language]);

  // Garde-fou : si le moteur ne signale jamais la version demandée (source
  // introuvable sans avis), la barre ne doit pas rester pleine pour toujours.
  useEffect(() => {
    if (!attente) return;
    const minuteur = window.setTimeout(() => setAttente(null), ATTENTE_MAX_MS);
    return () => window.clearTimeout(minuteur);
  }, [attente]);

  const choisir = (valeur: string, auto = false) => {
    if (!auto) onPause();
    if (valeur === effective && attente === null) return;
    const state = store.getState();
    state.setVariantSelector(
      { ...state.context.selector, [axe.axis.id]: valeur },
      reduit ? ambiances.fonduReduitSecondes : auto ? ambiances.fonduRotationSecondes : ambiances.fonduSecondes,
    );
    // Même clé que le rig de transition : la scène et le sélecteur sérialisé.
    setAttente({ valeur, cle: `${sceneId}|${JSON.stringify(store.getState().context.selector)}`, auto });
  };

  // La rotation : 5 s d'affichage RÉEL de la version courante (pas de chargement
  // compté), puis la suivante. Rien tant qu'une version charge.
  const affichee = progression.status === "shown" && progression.sourceKey?.startsWith(`${sceneId}|`) === true;
  useEffect(() => {
    if (enPause || attente !== null || !affichee || axe.valeurs.length < 2) return;
    const ici = axe.valeurs.findIndex((v) => v.id === effective);
    const suivante = axe.valeurs[(ici + 1) % axe.valeurs.length]!;
    const minuteur = window.setTimeout(() => choisir(suivante.id, true), ambiances.rotationSecondes * 1000);
    return () => window.clearTimeout(minuteur);
    // `choisir` lit l'état du rendu courant ; le minuteur repart à chaque version affichée.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enPause, attente, affichee, effective]);

  const surTouche = (event: KeyboardEvent<HTMLDivElement>) => {
    const boutons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-ambiance]")];
    const ici = boutons.indexOf(document.activeElement as HTMLButtonElement);
    if (ici < 0) return;
    const dernier = boutons.length - 1;
    const cible =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? dernier
          : event.key === "ArrowRight" || event.key === "ArrowDown"
            ? Math.min(dernier, ici + 1)
            : event.key === "ArrowLeft" || event.key === "ArrowUp"
              ? Math.max(0, ici - 1)
              : null;
    if (cible === null) return;
    event.preventDefault();
    boutons[cible]!.focus();
    boutons[cible]!.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  const nombre = axe.valeurs.length;
  const deborde = mobile ? nombre > 4 : nombre > 6;
  const icones: Record<string, string> = (ambiances.icones as Record<string, Record<string, string>>)[axe.axis.id] ?? {};

  return (
    <div
      role="group"
      aria-label={intitule}
      onKeyDown={surTouche}
      className={reduit ? "va-barre va-reduit" : "va-barre"}
      style={{
        ...barre,
        width: mobile ? "100%" : "auto",
        flexDirection: mobile ? "column" : "row",
        alignItems: mobile ? "stretch" : "center",
        gap: mobile ? 6 : 10,
        padding: mobile ? "8px 6px 6px" : 6,
      }}
    >
      <div style={{ ...intituleCadre, padding: mobile ? "2px 8px" : "0 6px 0 12px" }}>
        <span aria-hidden style={losange} />
        <span style={intituleTexte}>{intitule}</span>
      </div>
      <div
        ref={piste}
        className="va-piste"
        style={{
          ...pisteStyle,
          ...(deborde
            ? {
                maskImage: "linear-gradient(90deg, #000 85%, transparent)",
                WebkitMaskImage: "linear-gradient(90deg, #000 85%, transparent)",
              }
            : null),
        }}
      >
        {axe.valeurs.map((value) => {
          const choisi = value.id === (attente?.valeur ?? effective);
          const charge = attente?.valeur === value.id;
          const fraction = charge && concerne ? progression.fraction : 0;
          return (
            <button
              key={value.id}
              type="button"
              data-ambiance=""
              className="va-bouton"
              aria-pressed={choisi}
              tabIndex={choisi ? 0 : -1}
              onClick={() => choisir(value.id)}
              style={{
                ...bouton,
                flex: mobile && !deborde ? "1 1 0" : "0 0 auto",
                minWidth: mobile ? (deborde ? 84 : 0) : 0,
                minHeight: mobile ? 60 : 44,
                flexDirection: mobile ? "column" : "row",
                gap: mobile ? 5 : 8,
                padding: mobile ? "8px 6px 9px" : "0 16px",
                fontSize: mobile ? 12.5 : 14,
                ...(choisi ? boutonChoisi : null),
              }}
            >
              <IconeAmbianceSvg nom={icones[value.id]} taille={mobile ? 20 : 18} />
              <span style={{ textWrap: "balance" }}>{pickText(value.label, language) ?? value.id}</span>
              {/* Barre de chargement : une information, pas un effet — gardée même mouvement réduit. */}
              <span aria-hidden style={{ ...chargeCadre, opacity: charge ? 1 : 0 }}>
                <span style={{ ...chargeBarre, transform: `scaleX(${fraction})` }} />
              </span>
            </button>
          );
        })}
      </div>
      <div aria-live="polite" className="splash-sr">
        {annonce}
      </div>
    </div>
  );
}

/* --- styles (valeurs du handoff) ------------------------------------------ */

const voileBas: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: "42%",
  background: "linear-gradient(0deg, rgba(11,19,16,.55), rgba(11,19,16,0))",
  pointerEvents: "none",
};

/** Le conteneur ne capte rien : le glisser de la caméra passe à côté de la barre. */
const pile: CSSProperties = {
  position: "absolute",
  left: "var(--va-gauche, var(--va-marge, 12px))",
  right: "var(--va-marge, 12px)",
  bottom: "var(--va-bas, calc(16px + env(safe-area-inset-bottom)))",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  pointerEvents: "none",
};

const barre: CSSProperties = {
  pointerEvents: "auto",
  boxSizing: "border-box",
  maxWidth: "100%",
  display: "flex",
  background: "rgba(16,26,23,.66)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  boxShadow: "0 10px 30px rgba(8,14,12,.35)",
  fontFamily: POLICE_UI_SPLASH,
  color: COULEURS.creme,
};

const intituleCadre: CSSProperties = { display: "flex", alignItems: "center", gap: 9, flex: "none" };

const losange: CSSProperties = {
  width: 5,
  height: 5,
  transform: "rotate(45deg)",
  background: COULEURS.sauge,
  flex: "none",
};

const intituleTexte: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  color: "#E3D9C8",
  whiteSpace: "nowrap",
};

const pisteStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  gap: 4,
  overflowX: "auto",
  scrollbarWidth: "none",
  maxWidth: "100%",
};

const bouton: CSSProperties = {
  appearance: "none",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: 2,
  background: "transparent",
  color: COULEURS.texteRepos,
  fontFamily: POLICE_UI_SPLASH,
  fontWeight: 500,
  lineHeight: 1.2,
  textAlign: "center",
  cursor: "pointer",
};

const boutonChoisi: CSSProperties = { background: COULEURS.creme, color: COULEURS.vert };

const chargeCadre: CSSProperties = {
  position: "absolute",
  left: 8,
  right: 8,
  bottom: 3,
  height: 2,
  overflow: "hidden",
};

const chargeBarre: CSSProperties = {
  display: "block",
  height: "100%",
  background: COULEURS.sauge,
  transformOrigin: "left",
  transition: "transform .2s linear",
};
